import DetailModal from "@/components/DetailModal";
import TabDocTable from "@/components/TabDocTable";
import { moiFetch, moiSave, ApproveType } from "@/apis/reimburse";
import { RequestContentWrap } from "../ApprovalDetail/style";
import ApproveHeader from "../DetailAndApprove/ApproveHeader";
import RequirementSelect from "../RequirementSelect";
import usePage from "@/layout/hooks/usePage";
import { PageId } from "@/types/page";
import { getUserAvatarTip, getUserName } from "@/utils/user";
import WMSAvatar from "@/styles/WMSAvatar";
import { numFormmater } from "@/pages/Requirement/util";
import { Requirement, RequirementItem } from "@/types/requirement";
import {
  AddMoiRequest,
  AddMoiRequestItem,
  ApproveStatusEnum,
  ApproveStepStatusEnum,
  ApproveTypeEnum,
  AssignTypeEnum,
  MoiItem,
  MoiReimbursRequest,
  OverIssueReasonTypeEnum,
  ReimbursRequest,
  ReimbursRequestStep,
} from "@/types/reimburse";
import { AnyObject } from "antd/es/_util/type";
import { ColumnsType } from "antd/es/table";
import {
  App,
  Button,
  Descriptions,
  Flex,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Spin,
  Tag,
  Timeline,
  Tooltip,
  Typography,
  notification,
} from "antd";
import {
  CheckCircleFilled,
  ClockCircleFilled,
  CloseCircleFilled,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  RollbackOutlined,
} from "@ant-design/icons";
import { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { getRequirement, batchItems } from "@/apis/requirement";
import SText from "@/styles/SText";

interface DetailMaterialOverIssueProps {
  detail: ReimbursRequest;
  open: boolean;
  onClose: () => void;
  onSuccess: (data: ReimbursRequest) => void;
  /** 所有者可编辑入口：为 true 时允许编辑（组件内部仍按草稿/退回态校验）。默认按 pageId===REIMBURSE_MINE 判定。 */
  editable?: boolean;
}

interface MoiItemFormValues {
  requirementItemId: string;
  productId: string;
  overCount: number;
  reasonType: OverIssueReasonTypeEnum;
  reason?: string;
}

// Keep the item merge/validation/save mapping local to this feature file so the
// behavior stays close to the only component that owns it.
export const upsertMoiItem = (items: MoiItem[], nextItem: MoiItem) => {
  const currentIndex = items.findIndex((item) => item.id === nextItem.id);
  if (currentIndex === -1) {
    return [...items, nextItem];
  }

  const nextItems = [...items];
  nextItems[currentIndex] = nextItem;
  return nextItems;
};

export const hasInvalidMoiItem = (items: MoiItem[]) =>
  items.some(
    (item) =>
      !item.requirementItemId ||
      !item.productId ||
      typeof item.overCount !== "number" ||
      item.overCount <= 0 ||
      !item.reasonType,
  );

// moiSave accepts a flattened request body, while the detail page keeps the
// fetched application metadata and item list together in one view model.
export const buildMoiSavePayload = (
  detail: MoiReimbursRequest,
): AddMoiRequest => ({
  id: detail.application.id,
  title: detail.application.title,
  content: detail.application.content,
  requirementId: detail.items[0]?.requirementId ?? "",
  items: detail.items.map((item) => ({
    requirementItemId: item.requirementItemId,
    productId: item.productId,
    overCount: item.overCount,
    reasonType: item.reasonType,
    reason: item.reason || "",
  })),
});

const createDraftMoiItemId = () =>
  `temp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const DetailMaterialOverIssue: FC<DetailMaterialOverIssueProps> = ({
  detail,
  open,
  onClose,
  onSuccess,
  editable,
}) => {
  const { t } = useTranslation();
  const { pageId } = usePage();
  const { Text } = Typography;
  const { modal } = App.useApp();

  const [moiDetail, setMoiDetail] = useState<MoiReimbursRequest>({
    application: detail,
    items: [],
  });
  const [loading, setLoading] = useState(false);
  const [hasUpdate, setHasUpdate] = useState(false);
  const [openItemModal, setOpenItemModal] = useState(false);
  const [editingItem, setEditingItem] = useState<MoiItem | null>(null);
  // 关联需求单详情只在详情头部展示若干上下文字段，不混入超领单主状态。
  const [requirementDetail, setRequirementDetail] = useState<Requirement | null>(null);
  const [selectedRequirementItem, setSelectedRequirementItem] =
    useState<RequirementItem | null>(null);
  const [itemForm] = Form.useForm<MoiItemFormValues>();
  // 超领明细接口不返回完整物料快照，这里用 requirementItemId 建一个本地索引给表格列复用。
  const [itemProductMap, setItemProductMap] = useState<
    Record<string, RequirementItem>
  >({});

  const application = moiDetail.application;
  const currentRequirementId =
    editingItem?.requirementId || moiDetail.items[0]?.requirementId || "";

  // 详情接口先拿超领单自身数据；如果存在关联需求单，再补一次需求详情给头部描述区使用。
  useEffect(() => {
    setMoiDetail({
      application: detail,
      items: [],
    });
    setHasUpdate(false);
    setItemProductMap({});
    setRequirementDetail(null);

    if (!detail.id) {
      return;
    }

    setLoading(true);
    moiFetch(detail.id)
      .then(async (res) => {
        setMoiDetail(res);
        // 同一张超领单的明细都属于同一个 requirementId，取第一条即可还原头部上下文。
        const requirementId = res.items[0]?.requirementId;
        if (!requirementId) {
          return;
        }
        const requirement = await getRequirement(requirementId);
        setRequirementDetail(requirement);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [detail]);

  useEffect(() => {
    if (!openItemModal) {
      itemForm.resetFields();
      setSelectedRequirementItem(null);
      return;
    }

    if (editingItem) {
      itemForm.setFieldsValue({
        requirementItemId: editingItem.requirementItemId,
        productId: editingItem.productId,
        overCount: editingItem.overCount,
        reasonType: editingItem.reasonType,
        reason: editingItem.reason || "",
      });
      return;
    }

    itemForm.setFieldsValue({
      productId: "",
    } as Partial<MoiItemFormValues>);
  }, [editingItem, itemForm, openItemModal]);

  // 用 requirementItemId 反查需求清单项，补全物料的 code/name/model 用于列表展示
  useEffect(() => {
    const ids = moiDetail.items
      .map((item) => item.requirementItemId)
      .filter(Boolean);
    const missing = ids.filter((id) => !itemProductMap[id]);
    if (missing.length === 0) {
      return;
    }
    let active = true; // 明细或详情切换后，忽略旧请求回写。
    batchItems(missing)
      .then((res) => {
        if (!active) {
          return;
        }
        const list = Array.isArray(res) ? res : [];
        setItemProductMap((prev) => {
          const next = { ...prev };
          list.forEach((ri) => {
            if (ri.id) {
              next[ri.id] = ri;
            }
          });
          return next;
        });
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [moiDetail.items, itemProductMap]);

  const sortedSteps = useMemo(() => {
    if (!application.steps || application.steps.length === 0) {
      return [];
    }
    return [...application.steps].sort((a, b) => a.stepNo - b.stepNo);
  }, [application.steps]);

  // 是否「所有者可编辑上下文」：新入口用 editable 显式开启，报销入口沿用 pageId 判定。
  // 据此派生 canEdit，并把对应 pageId 传给 ApproveHeader（让其保存/提交出现、审批按钮不出现）。
  const isOwnerContext = editable ?? pageId === PageId.REIMBURSE_MINE;

  const canEdit = useMemo(
    () =>
      isOwnerContext &&
      [ApproveStatusEnum.DRAFT, ApproveStatusEnum.RETURNED].includes(
        application.status,
      ),
    [application.status, isOwnerContext],
  );

  const getStepStatusInfo = (step: ReimbursRequestStep) => {
    switch (step.stepStatus) {
      case ApproveStepStatusEnum.PENDING:
        return {
          icon: <ClockCircleFilled style={{ color: "#1890ff" }} />,
          text: t("reimburse.statusPending"),
          color: "processing",
        };
      case ApproveStepStatusEnum.APPROVED:
        return {
          icon: <CheckCircleFilled style={{ color: "#52c41a" }} />,
          text: t("reimburse.statusApproved"),
          color: "success",
        };
      case ApproveStepStatusEnum.RETURNED:
        return {
          icon: <RollbackOutlined style={{ color: "#faad14" }} />,
          text: t("reimburse.statusReturned"),
          color: "warning",
        };
      case ApproveStepStatusEnum.REJECTED:
        return {
          icon: <CloseCircleFilled style={{ color: "#ff4d4f" }} />,
          text: t("reimburse.statusRejected"),
          color: "error",
        };
      default:
        return {
          icon: <ClockCircleFilled style={{ color: "#d9d9d9" }} />,
          text: "-",
          color: "default",
        };
    }
  };

  const getAssignTypeText = (assignType: AssignTypeEnum) => {
    switch (assignType) {
      case AssignTypeEnum.SPECIFIC_ORG_ANYONE:
        return t("reimburse.specificOrgAnyone");
      case AssignTypeEnum.APPLICANT_ORG_ANYONE:
        return t("reimburse.applicantOrgAnyone");
      case AssignTypeEnum.SPECIFIC_USERS:
        return t("reimburse.specificUsers");
      default:
        return "-";
    }
  };

  const getApproveTypeText = (approveType: ApproveTypeEnum | null) => {
    switch (approveType) {
      case ApproveTypeEnum.IN_ORDER:
        return t("reimburse.inOrder");
      case ApproveTypeEnum.ANY:
        return t("reimburse.any");
      case ApproveTypeEnum.LAST:
        return t("reimburse.last");
      default:
        return "-";
    }
  };

  const getReasonTypeText = (reasonType: OverIssueReasonTypeEnum) => {
    switch (reasonType) {
      case OverIssueReasonTypeEnum.MISTAKE:
        return t("reimburse.reasonMistake");
      case OverIssueReasonTypeEnum.WASTE:
        return t("reimburse.reasonWaste");
      case OverIssueReasonTypeEnum.OTHER:
        return t("reimburse.reasonOther");
      default:
        return "-";
    }
  };

  const handleConfirm = (
    data: ReimbursRequest,
    confirmType: ApproveType | undefined,
  ) => {
    onSuccess(data);
    onClose();
  };
  // 保存需求清单
  const handleSave = async () => {
    if (moiDetail.items.length === 0) {
      notification.error({
        placement: "top",
        message: t("common.tip"),
        description: t("reimburse.atLeastOne"),
      });
      throw new Error("MOI_ITEMS_EMPTY");
    }

    if (hasInvalidMoiItem(moiDetail.items)) {
      notification.error({
        placement: "top",
        message: t("common.tip"),
        description: t("common.validateFailed"),
      });
      throw new Error("MOI_ITEMS_INVALID");
    }

    const payload = buildMoiSavePayload(moiDetail);
    const requestItems: AddMoiRequestItem[] = payload.items;

    if (!payload.requirementId || requestItems.length === 0) {
      notification.error({
        placement: "top",
        message: t("common.tip"),
        description: t("reimburse.atLeastOne"),
      });
      throw new Error("MOI_REQUIREMENT_ID_MISSING");
    }

    const res = await moiSave(payload);
    setMoiDetail((prev) => ({
      ...prev,
      application: res,
    }));
    return res;
  };

  // 添加需求清单项
  const handleAddItem = () => {
    if (!currentRequirementId) {
      notification.error({
        placement: "top",
        message: t("common.tip"),
        description: `${t("common.plSelect")}${t("layout.requirementList")}`,
      });
      return;
    }

    setEditingItem(null);
    setOpenItemModal(true);
  };

  const handleEditItem = (item: MoiItem) => {
    setEditingItem(item);
    setOpenItemModal(true);
  };

  // 删除需求清单项
  const handleDeleteItem = (item: MoiItem) => {
    if (moiDetail.items.length <= 1) {
      notification.warning({
        placement: "top",
        message: t("common.tip"),
        description: t("reimburse.atLeastOne"),
      });
      return;
    }

    modal.confirm({
      title: t("common.rmTip"),
      content: t("common.rmContent"),
      onOk: () => {
        setMoiDetail((prev) => ({
          ...prev,
          items: prev.items.filter((currentItem) => currentItem.id !== item.id),
        }));
        setHasUpdate(true);
      },
    });
  };

  // 需求清单项弹窗确认
  const handleItemModalOk = async () => {
    const values = (await itemForm.validateFields()) as MoiItemFormValues;
    const requirementId = editingItem?.requirementId || currentRequirementId;

    if (!requirementId) {
      notification.error({
        placement: "top",
        message: t("common.tip"),
        description: `${t("common.plSelect")}${t("layout.requirementList")}`,
      });
      return;
    }

    const nextItem: MoiItem = {
      id: editingItem?.id || createDraftMoiItemId(),
      applicationId: editingItem?.applicationId || application.id,
      requirementId,
      requirementItemId: values.requirementItemId,
      productId: values.productId,
      orderAmount: selectedRequirementItem
        ? Number(selectedRequirementItem.useAmount || 0)
        : editingItem?.orderAmount || 0,
      outCount: selectedRequirementItem
        ? Number(selectedRequirementItem.outCount || 0)
        : editingItem?.outCount || 0,
      overCount: values.overCount,
      reasonType: values.reasonType,
      reason: values.reason || "",
      picked: editingItem?.picked || 0,
    };

    setMoiDetail((prev) => ({
      ...prev,
      items: upsertMoiItem(prev.items, nextItem),
    }));
    setHasUpdate(true);
    setOpenItemModal(false);
    setEditingItem(null);
  };

  // 明细行里优先展示补齐后的物料信息，避免列表只能看到 requirementItemId 这样的内部标识。
  const itemColumns: ColumnsType<MoiItem> = [
    {
      title: t("product.code"),
      dataIndex: "requirementItemId",
      width: 140,
      render: (_, record) =>
        itemProductMap[record.requirementItemId]?.product?.code || "-",
    },
    {
      title: t("product.name"),
      dataIndex: "requirementItemId",
      width: 160,
      render: (_, record) =>
        itemProductMap[record.requirementItemId]?.product?.name || "-",
    },
    {
      title: t("product.brand1"),
      dataIndex: "requirementItemId",
      width: 200,
      ellipsis: true,
      render: (_, record) => (
        <SText
          text={itemProductMap[record.requirementItemId]?.product?.model || "-"}
          style={{ display: "inline-block", width: 200 }}
        />
      ),
    },
    {
      title: t("reimburse.orderAmount"),
      dataIndex: "orderAmount",
      width: 120,
      render: (value) => numFormmater(Number(value || 0)),
    },
    {
      title: t("requirement.outCount"),
      dataIndex: "outCount",
      width: 120,
      render: (value) => numFormmater(Number(value || 0)),
    },
    {
      title: t("reimburse.overCount"),
      dataIndex: "overCount",
      width: 120,
      render: (value) => numFormmater(Number(value || 0)),
    },
    {
      title: t("reimburse.reasonType"),
      dataIndex: "reasonType",
      width: 160,
      render: (value: OverIssueReasonTypeEnum) => getReasonTypeText(value),
    },
    {
      title: t("reimburse.reason"),
      dataIndex: "reason",
      width: 200,
      ellipsis: true,
      render: (value) => <SText text={value || "-"} style={{ display: "inline-block", width: 200 }} />,
    },
    {
      title: t("reimburse.picked"),
      dataIndex: "picked",
      width: 140,
      render: (value: number) => (
        <Tag color={value ? "success" : "default"}>
          {value ? t("reimburse.pickedYes") : t("reimburse.pickedNo")}
        </Tag>
      ),
    },
    {
      title: t("common.opt"),
      width: 120,
      fixed: "right",
      hidden: !canEdit,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditItem(record)}
          >
            {t("common.edit")}
          </Button>
          <Button
            type="link"
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteItem(record)}
          >
            {t("common.del")}
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <DetailModal
      logBookId={application.logbookId}
      modalProps={{
        width: "100vw",
        height: "100vh",
        styles: {
          content: { height: "100vh" },
          body: { height: "calc(100vh - 60px)" },
        },
        title: (
          <ApproveHeader
            detail={application}
            onConfirm={handleConfirm}
            pageId={isOwnerContext ? PageId.REIMBURSE_MINE : pageId}
            onSave={handleSave}
            hasUpdate={hasUpdate}
            setHasUpdate={setHasUpdate}
          />
        ),
        open,
        onCancel: onClose,
        footer: null,
      }}
    >
      <RequestContentWrap>
        {sortedSteps.length > 0 && (
          <div className="timeline-left-panel">
            {application.approveType && (
              <div className="approvetype">
                <Typography.Title
                  level={5}
                  style={{ marginTop: -12, marginBottom: 12 }}
                >
                  {getApproveTypeText(application.approveType)}
                </Typography.Title>
              </div>
            )}
            <Timeline
              items={sortedSteps.map((step, index) => {
                const statusInfo = getStepStatusInfo(step);
                return {
                  dot: statusInfo.icon,
                  color:
                    step.stepStatus === ApproveStepStatusEnum.APPROVED
                      ? "#52c41a"
                      : step.stepStatus === ApproveStepStatusEnum.RETURNED
                        ? "#faad14"
                        : step.stepStatus === ApproveStepStatusEnum.REJECTED
                          ? "#ff4d4f"
                          : step.stepStatus === ApproveStepStatusEnum.PENDING
                            ? "#1890ff"
                            : "#d9d9d9",
                  children: (
                    <div className="timeline-item-content">
                      <div className="timeline-item-top">
                        <Text strong>{index + 1}.</Text>
                        <Tag
                          color={statusInfo.color}
                          style={{ fontSize: 12, padding: "0 4px" }}
                        >
                          {statusInfo.text}
                        </Tag>
                      </div>
                      <div className="timeline-item-middle">
                        <Text>
                          {t("reimburse.approveTypeLabel")}:
                          {getAssignTypeText(step.assignType)}
                        </Text>
                        {step.assignType === AssignTypeEnum.SPECIFIC_USERS &&
                        step.candidates &&
                        step.candidates.length > 0 ? (
                          <div className="user-warp">
                            <span>{t("reimburse.approver")}: </span>
                            {step.candidates.map((user) => (
                              <div className="user-name" key={user.id}>
                                <WMSAvatar
                                  uniChar={user.id}
                                  avatarId={user.avatar}
                                  creator={user}
                                  alt={getUserAvatarTip(user)}
                                  size={20}
                                />
                                <span>{getUserName(user)}</span>
                              </div>
                            ))}
                          </div>
                        ) : step.org ? (
                          <div>
                            <span>{t("reimburse.approveOrg")}：</span>
                            <Tag color="magenta">{step.org.name}</Tag>
                          </div>
                        ) : (
                          <>{t("reimburse.approver")}: -</>
                        )}
                      </div>
                      {step.approvalComment && (
                        <div className="timeline-item-bottom">
                          <Tooltip
                            title={step.approvalComment}
                            placement="rightTop"
                          >
                            <span>
                              {t("reimburse.approveRemark")}:
                              {step.approvalComment}
                            </span>
                          </Tooltip>
                        </div>
                      )}
                      {step.stepStatus !== ApproveStepStatusEnum.PENDING && (
                        <div className="timeline-item-bottom">
                          <span>{t("reimburse.approveTime")}：</span>
                          {dayjs(step.updatedAt).format("YYYY-MM-DD HH:mm:ss")}
                        </div>
                      )}
                    </div>
                  ),
                };
              })}
            />
          </div>
        )}

        <div style={{ flex: 1, overflow: "auto" }}>
          <div style={{ overflow: "auto", paddingRight: 16 }}>
            {loading ? (
              <Spin style={{ marginTop: 16, width: "100%", height: 240 }} />
            ) : (
              <>
                <Descriptions
                  column={3}
                  size="small"
                  style={{ marginBottom: 16 }}
                >
                  <Descriptions.Item
                    span={3}
                    label={
                      <div className="desc-label">{t("reimburse.title")}</div>
                    }
                  >
                    {canEdit ? (
                      <Input
                        status={application.title ? undefined : "error"}
                        value={application.title}
                        onChange={(e) => {
                          setMoiDetail((prev) => ({
                            ...prev,
                            application: {
                              ...prev.application,
                              title: e.target.value,
                            },
                          }));
                          setHasUpdate(true);
                        }}
                        placeholder={t("reimburse.plTitle")}
                        maxLength={200}
                      />
                    ) : (
                      application.title
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item
                    span={3}
                    label={
                      <div className="desc-label">{t("reimburse.content")}</div>
                    }
                    style={{ paddingBottom: canEdit ? 24 : undefined }}
                  >
                    {canEdit ? (
                      <Input.TextArea
                        status={application.content ? undefined : "error"}
                        value={application.content}
                        onChange={(e) => {
                          setMoiDetail((prev) => ({
                            ...prev,
                            application: {
                              ...prev.application,
                              content: e.target.value,
                            },
                          }));
                          setHasUpdate(true);
                        }}
                        placeholder={t("reimburse.plContent")}
                        autoSize={{ minRows: 2, maxRows: 2 }}
                        maxLength={500}
                        showCount
                      />
                    ) : (
                      application.content || "-"
                    )}
                  </Descriptions.Item>
                  {application.creator && (
                    <Descriptions.Item
                      label={
                        <div className="desc-label">
                          {t("reimburse.applicant")}
                        </div>
                      }
                    >
                      <Flex align="center" gap={4}>
                        <WMSAvatar
                          uniChar={application.creator?.id ?? "1"}
                          avatarId={application.creator?.avatar ?? ""}
                          creator={application.creator}
                          alt={getUserAvatarTip(application.creator)}
                          size={24}
                        />
                        <span>{getUserName(application.creator)}</span>
                      </Flex>
                    </Descriptions.Item>
                  )}
                  {/* 这两个字段来自关联需求单详情，不在超领申请单主表里直接返回。 */}
                  <Descriptions.Item
                    label={
                      <div className="desc-label">{t("sale.internalNumber")}</div>
                    }
                  >
                    {requirementDetail?.saleOrder?.innerNumber || "-"}
                  </Descriptions.Item>
                  <Descriptions.Item
                    label={
                      <div className="desc-label">{t("sale.requirement")}</div>
                    }
                  >
                    {requirementDetail?.number || "-"}
                  </Descriptions.Item>
                  <Descriptions.Item
                    label={
                      <div className="desc-label">{t("common.createTime")}</div>
                    }
                  >
                    {dayjs(application.createdAt).format("YYYY-MM-DD HH:mm:ss")}
                  </Descriptions.Item>
                  <Descriptions.Item
                    label={
                      <div className="desc-label">
                        {t("reimburse.submitTime")}
                      </div>
                    }
                  >
                    {application.submitAt
                      ? dayjs(application.submitAt).format(
                          "YYYY-MM-DD HH:mm:ss",
                        )
                      : "-"}
                  </Descriptions.Item>
                  <Descriptions.Item
                    label={
                      <div className="desc-label">
                        {t("reimburse.completeTime")}
                      </div>
                    }
                  >
                    {application.completeAt
                      ? dayjs(application.completeAt).format(
                          "YYYY-MM-DD HH:mm:ss",
                        )
                      : "-"}
                  </Descriptions.Item>
                </Descriptions>

                <TabDocTable
                  tableName={t("reimburse.itemList")}
                  tableProps={{
                    columns: itemColumns as AnyObject[],
                    dataSource: moiDetail.items,
                    rowKey: "id",
                    size: "small",
                    scroll: { x: "max-content" },
                    pagination: { pageSize: canEdit ? 5 : 10 },
                  }}
                  docProps={{ isRead: true, forceReset: true }}
                  tabProps={{
                    tabBarExtraContent: canEdit ? (
                      <Flex justify="flex-end" align="center">
                        <Button
                          type="primary"
                          size="small"
                          icon={<PlusOutlined />}
                          onClick={handleAddItem}
                          disabled={!canEdit || !currentRequirementId}
                        >
                          {t("common.add")}
                        </Button>
                      </Flex>
                    ) : null,
                  }}
                />
              </>
            )}
          </div>
        </div>
      </RequestContentWrap>

      <Modal
        title={
          editingItem
            ? `${t("common.edit")}${t("reimburse.materialOverIssue")}`
            : `${t("common.add")}${t("reimburse.materialOverIssue")}`
        }
        open={openItemModal}
        onCancel={() => {
          setOpenItemModal(false);
          setEditingItem(null);
        }}
        onOk={handleItemModalOk}
        destroyOnClose
        maskClosable={false}
        width={720}
      >
        <Form
          form={itemForm}
          labelCol={{ style: { width: 120, textAlign: "right" } }}
          layout="horizontal"
        >
          <Form.Item name="productId" hidden>
            <Input />
          </Form.Item>
          <Form.Item
            name="requirementItemId"
            label={t("reimburse.requirementItemId")}
            rules={[
              {
                required: true,
                message: t("reimburse.requirementItemId"),
              },
            ]}
          >
            <RequirementSelect
              entityType="item"
              requirementId={currentRequirementId}
              excludeIds={
                moiDetail.items
                  .map((item) =>
                    item.id === editingItem?.id
                      ? undefined
                      : item.requirementItemId,
                  )
                  .filter(Boolean) as string[]
              }
              onEntityChange={(item) => {
                const nextItem = item as RequirementItem | null;
                setSelectedRequirementItem(nextItem);
                itemForm.setFieldValue("productId", nextItem?.productId || "");
              }}
              placeholder={t("reimburse.requirementItemId")}
            />
          </Form.Item>
          <Form.Item
            name="overCount"
            label={t("reimburse.overCount")}
            rules={[
              {
                required: true,
                message: t("reimburse.overCount"),
              },
              {
                validator: (_, value) =>
                  typeof value === "number" && value > 0
                    ? Promise.resolve()
                    : Promise.reject(new Error(t("reimburse.overCount"))),
              },
            ]}
          >
            <InputNumber changeOnWheel
              min={0.01}
              precision={0}
              style={{ width: "100%" }}
              placeholder={t("reimburse.overCount")}
            />
          </Form.Item>
          <Form.Item
            name="reasonType"
            label={t("reimburse.reasonType")}
            rules={[
              {
                required: true,
                message: t("reimburse.reasonType"),
              },
            ]}
          >
            <Select
              placeholder={t("reimburse.reasonType")}
              options={[
                {
                  label: t("reimburse.reasonMistake"),
                  value: OverIssueReasonTypeEnum.MISTAKE,
                },
                {
                  label: t("reimburse.reasonWaste"),
                  value: OverIssueReasonTypeEnum.WASTE,
                },
                {
                  label: t("reimburse.reasonOther"),
                  value: OverIssueReasonTypeEnum.OTHER,
                },
              ]}
            />
          </Form.Item>
          <Form.Item name="reason" label={t("reimburse.reason")}>
            <Input.TextArea
              autoSize={{ minRows: 2, maxRows: 4 }}
              showCount
              placeholder={t("reimburse.reason")}
              maxLength={500}
            />
          </Form.Item>
        </Form>
      </Modal>
    </DetailModal>
  );
};

export default DetailMaterialOverIssue;
